import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import PermissionGuard from './PermissionGuard';
import {
  MagnifyingGlassIcon,
  EyeIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  BookmarkIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const FormsAndTasks = () => {
  const navigate = useNavigate();
  const { checkPermission } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    search: ''
  });
  const [selectedStatuses, setSelectedStatuses] = useState([]); // Array of selected statuses
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  // Sorting state
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Header filter state
  const [headerFilters, setHeaderFilters] = useState({});
  const [filterDialog, setFilterDialog] = useState({ open: false, field: null });
  const [dateRangeFilters, setDateRangeFilters] = useState({});
  
  // Column visibility state
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnVisibilityPosition, setColumnVisibilityPosition] = useState({ top: null, right: null });
  const columnVisibilityButtonRef = useRef(null);

  // Save/Load View state
  const [savedViews, setSavedViews] = useState([]);
  const [showSaveViewDialog, setShowSaveViewDialog] = useState(false);
  const [showLoadViewDialog, setShowLoadViewDialog] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [loadingViews, setLoadingViews] = useState(false);
  const [currentViewId, setCurrentViewId] = useState(null);

  // Dynamic filter options state
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const [uniqueClients, setUniqueClients] = useState([]);
  const [numberRangeFilters, setNumberRangeFilters] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchSavedViews();
    loadDefaultView();
  }, []);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [filters, pagination.page, pagination.limit, selectedStatuses, sortField, sortDirection, headerFilters, dateRangeFilters, numberRangeFilters]);

  // Refetch stats when page becomes visible again (e.g. return from task/submission detail)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStats();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Fetch users and groups for Assigned To filter
  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/groups');
      setGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  // Save/Load View for Deadlines layout
  const fetchSavedViews = async () => {
    try {
      setLoadingViews(true);
      const response = await api.get('/user-views/deadlines');
      if (response.data.success && response.data.data) {
        setSavedViews(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching saved views:', error);
    } finally {
      setLoadingViews(false);
    }
  };

  const loadDefaultView = async () => {
    try {
      const response = await api.get('/user-views/deadlines/default');
      if (response.data.success && response.data.data) {
        applyView(response.data.data, true);
      }
    } catch (error) {
      console.error('Error loading default view:', error);
    }
  };

  const applyView = (view, silent = false) => {
    if (!view || !view.viewSettings) return;
    const { columnVisibility: viewColumnVisibility, sortField: viewSortField, sortDirection: viewSortDirection } = view.viewSettings;
    if (viewColumnVisibility && typeof viewColumnVisibility === 'object') {
      setColumnVisibility(prev => ({ ...prev, ...viewColumnVisibility }));
    }
    if (viewSortField) setSortField(viewSortField);
    if (viewSortDirection) setSortDirection(viewSortDirection);
    setCurrentViewId(view._id);
    if (!silent) toast.success(`View "${view.name}" loaded`);
  };

  const handleSaveView = async () => {
    if (!newViewName.trim()) {
      toast.error('Please enter a view name');
      return;
    }
    try {
      setSavingView(true);
      const response = await api.post('/user-views/deadlines', {
        name: newViewName.trim(),
        viewSettings: { columnVisibility, sortField, sortDirection },
        isDefault: saveAsDefault
      });
      if (response.data.success) {
        toast.success('View saved successfully');
        setShowSaveViewDialog(false);
        setNewViewName('');
        setSaveAsDefault(false);
        fetchSavedViews();
        if (saveAsDefault) setCurrentViewId(response.data.data._id);
      }
    } catch (error) {
      console.error('Error saving view:', error);
      toast.error(error.response?.data?.message || 'Failed to save view');
    } finally {
      setSavingView(false);
    }
  };

  const handleLoadView = (view) => {
    applyView(view);
    setShowLoadViewDialog(false);
  };

  const handleDeleteView = async (viewId) => {
    if (!window.confirm('Are you sure you want to delete this view?')) return;
    try {
      const response = await api.delete(`/user-views/${viewId}`);
      if (response.data.success) {
        toast.success('View deleted successfully');
        fetchSavedViews();
        if (currentViewId === viewId) setCurrentViewId(null);
      }
    } catch (error) {
      console.error('Error deleting view:', error);
      toast.error('Failed to delete view');
    }
  };

  const handleSetDefaultView = async (viewId) => {
    try {
      const response = await api.put(`/user-views/${viewId}/default`);
      if (response.data.success) {
        toast.success('Default view set successfully');
        fetchSavedViews();
      }
    } catch (error) {
      console.error('Error setting default view:', error);
      toast.error('Failed to set default view');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setFilterLoading(true);
      const params = new URLSearchParams();
      
      // Add status filters (multiple statuses can be selected)
      if (selectedStatuses.length > 0) {
        selectedStatuses.forEach(status => {
          params.append('status', status);
        });
      } else if (filters.status) {
        params.append('status', filters.status);
      }
      
      // Add type filter
      if (filters.type) {
        params.append('type', filters.type);
      }
      
      // Add search filter
      if (filters.search) {
        params.append('search', filters.search);
      }
      
      // Add sorting parameters
      params.append('sortField', sortField);
      params.append('sortDirection', sortDirection);
      
      // Add header filters
      Object.keys(headerFilters).forEach(key => {
        const filterValue = headerFilters[key];
        if (Array.isArray(filterValue) && filterValue.length > 0) {
          filterValue.forEach(value => {
            params.append(`filters[${key}][]`, value);
          });
        } else if (filterValue && typeof filterValue === 'string' && filterValue.trim() !== '') {
          params.append(`filters[${key}]`, filterValue.trim());
        }
      });
      
      // Add date range filters
      Object.keys(dateRangeFilters).forEach(key => {
        const range = dateRangeFilters[key];
        if (range && (range.from || range.to)) {
          if (range.from && range.from.trim() !== '') {
            params.append(`filters[${key}][from]`, range.from.trim());
          }
          if (range.to && range.to.trim() !== '') {
            params.append(`filters[${key}][to]`, range.to.trim());
          }
        }
      });

      // Add number range filters (for Days Left)
      Object.keys(numberRangeFilters).forEach(key => {
        const range = numberRangeFilters[key];
        if (range && (range.min !== undefined || range.max !== undefined)) {
          if (range.min !== undefined && range.min !== '') {
            params.append(`filters[${key}][min]`, range.min.toString());
          }
          if (range.max !== undefined && range.max !== '') {
            params.append(`filters[${key}][max]`, range.max.toString());
          }
        }
      });
      
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());

      const response = await api.get(`/forms-and-tasks?${params.toString()}`);
      const fetchedData = response.data.data;
      setData(fetchedData);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));

      // Extract unique statuses and clients from data
      const statusSet = new Set();
      const clientSet = new Set();
      fetchedData.forEach(item => {
        if (item.status) statusSet.add(item.status);
        if (item.client) clientSet.add(item.client);
      });
      setUniqueStatuses(Array.from(statusSet).sort());
      setUniqueClients(Array.from(clientSet).sort());
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error('Error fetching forms and tasks:', error);
    } finally {
      setLoading(false);
      setFilterLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/forms-and-tasks/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Toggle status filter
  const toggleStatus = (status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUpIcon className="h-4 w-4 text-blue-600" />
      : <ChevronDownIcon className="h-4 w-4 text-blue-600" />;
  };

  // Header filter functions
  const clearHeaderFilter = (field) => {
    setFilterLoading(true);
    setHeaderFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const clearDateRangeFilter = (fieldKey) => {
    setFilterLoading(true);
    setDateRangeFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[fieldKey];
      return newFilters;
    });
  };

  const clearNumberRangeFilter = (fieldKey) => {
    setFilterLoading(true);
    setNumberRangeFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[fieldKey];
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setFilterLoading(true);
    setHeaderFilters({});
    setDateRangeFilters({});
    setNumberRangeFilters({});
    setSelectedStatuses([]);
    setFilters({ type: '', status: '', search: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Define table fields
  const tableFields = [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      filterable: true,
      render: (item) => `#${item.id.slice(-6)}`
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      filterable: true,
      fieldType: 'dropdown',
      fieldOptions: ['Form', 'Task', 'Custom Page'],
      render: (item) => {
        const TypeIcon = getTypeIcon(item.type, item);
        return (
          <div className="flex items-center">
            <TypeIcon className="h-4 w-4 mr-2 text-gray-400" />
            <span className="text-sm text-gray-900">{item.type}</span>
          </div>
        );
      }
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      filterable: true,
      render: (item) => <span className="font-medium">{item.name}</span>
    },
    {
      key: 'client',
      label: 'Client',
      sortable: true,
      filterable: true,
      fieldType: 'dropdown',
      fieldOptions: uniqueClients, // Dynamic options from data
      render: (item) => item.client || '-'
    },
    {
      key: 'pendingDaysLeft',
      label: 'Days Left',
      sortable: true,
      filterable: true,
      fieldType: 'numberRange',
      render: (item) => (
        <span className={`text-sm ${getDaysLeftColor(item.pendingDaysLeft)}`}>
          {formatDaysLeft(item.pendingDaysLeft)}
        </span>
      )
    },
    {
      key: 'assignedUsers',
      label: 'Assigned To',
      sortable: true,
      filterable: true,
      fieldType: 'userGroup',
      render: (item) => (
        <div>
          {item.assignedUsers !== 'None' && (
            <div className="text-sm text-gray-900">{item.assignedUsers}</div>
          )}
          {item.assignedGroups !== 'None' && (
            <div className="text-xs text-gray-500">{item.assignedGroups}</div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      fieldType: 'dropdown',
      fieldOptions: uniqueStatuses, // Dynamic options from data
      render: (item) => {
        const StatusIcon = getStatusIcon(item.status);
        return (
          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {item.status.replace('_', ' ')}
          </span>
        );
      }
    }
  ];

  // Get visible fields based on column visibility
  const getVisibleFields = () => {
    return tableFields.filter(field => {
      if (columnVisibility[field.key] === undefined) {
        return true; // Default to visible
      }
      return columnVisibility[field.key] !== false;
    });
  };

  // Get field options for dropdown filters
  const getFieldOptions = (fieldKey) => {
    const tableField = tableFields.find(f => f.key === fieldKey);
    if (fieldKey === 'assignedUsers') {
      // Combine users and groups for Assigned To filter
      const userOptions = users.map(u => ({ type: 'user', id: u._id, name: `${u.firstName} ${u.lastName}`, email: u.email }));
      const groupOptions = groups.map(g => ({ type: 'group', id: g._id, name: g.name }));
      return [...userOptions, ...groupOptions];
    }
    return tableField?.fieldOptions || [];
  };

  // Sortable Header Component
  const SortableHeader = ({ field, onSort }) => {
    const isFilterActive = (field.fieldType === 'date' 
      ? (dateRangeFilters[field.key] && (dateRangeFilters[field.key].from || dateRangeFilters[field.key].to))
      : field.fieldType === 'numberRange'
      ? (numberRangeFilters[field.key] && (numberRangeFilters[field.key].min !== undefined || numberRangeFilters[field.key].max !== undefined))
      : field.fieldType === 'dropdown' || field.fieldType === 'userGroup'
      ? (Array.isArray(headerFilters[field.key]) ? headerFilters[field.key].length > 0 : headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== '')
      : (headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== ''));
    
    return (
      <div className="flex items-center justify-between">
        <button
          onClick={() => field.sortable && onSort(field.key)}
          className={`flex items-center space-x-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
            field.sortable ? 'cursor-pointer hover:text-gray-700' : 'cursor-default'
          }`}
        >
          <span className="text-xs">{field.label}</span>
          {field.sortable && getSortIcon(field.key)}
        </button>
        
        {field.filterable && (
          <button
            onClick={() => setFilterDialog({ open: true, field })}
            className={`p-1 rounded ${
              isFilterActive ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
            }`}
            title="Filter"
          >
            <FunnelIcon className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  };

  // Filter Dialog Component
  const FilterDialog = ({ field, onClose }) => {
    const [localFilterValue, setLocalFilterValue] = useState('');
    const [localSelectedOptions, setLocalSelectedOptions] = useState([]);
    const [localDateRange, setLocalDateRange] = useState({ from: '', to: '' });
    const [localNumberRange, setLocalNumberRange] = useState({ min: '', max: '' });

    useEffect(() => {
      if (field) {
        if (field.fieldType === 'date') {
          const range = dateRangeFilters[field.key] || { from: '', to: '' };
          setLocalDateRange(range);
        } else if (field.fieldType === 'numberRange') {
          const range = numberRangeFilters[field.key] || { min: '', max: '' };
          setLocalNumberRange(range);
        } else if (field.fieldType === 'dropdown' || field.fieldType === 'userGroup') {
          const filterValue = headerFilters[field.key];
          if (Array.isArray(filterValue)) {
            setLocalSelectedOptions(filterValue);
          } else if (filterValue) {
            setLocalSelectedOptions([filterValue]);
          } else {
            setLocalSelectedOptions([]);
          }
        } else {
          setLocalFilterValue(headerFilters[field.key] || '');
        }
      }
    }, [field]);

    const handleDropdownToggle = (option) => {
      setLocalSelectedOptions(prev => {
        // For userGroup, compare by id; for regular dropdown, compare by value
        const isSelected = field.fieldType === 'userGroup'
          ? prev.some(p => (typeof p === 'object' ? p.id : p) === (typeof option === 'object' ? option.id : option))
          : prev.includes(option);
        
        if (isSelected) {
          return prev.filter(v => {
            if (field.fieldType === 'userGroup') {
              const vId = typeof v === 'object' ? v.id : v;
              const optId = typeof option === 'object' ? option.id : option;
              return vId !== optId;
            }
            return v !== option;
          });
        } else {
          return [...prev, option];
        }
      });
    };

    const handleApply = () => {
      setFilterLoading(true);
      if (field.fieldType === 'date') {
        if (localDateRange.from || localDateRange.to) {
          setDateRangeFilters(prev => ({
            ...prev,
            [field.key]: localDateRange
          }));
        } else {
          clearDateRangeFilter(field.key);
        }
      } else if (field.fieldType === 'numberRange') {
        if (localNumberRange.min !== '' || localNumberRange.max !== '') {
          setNumberRangeFilters(prev => ({
            ...prev,
            [field.key]: localNumberRange
          }));
        } else {
          clearNumberRangeFilter(field.key);
        }
      } else if (field.fieldType === 'dropdown' || field.fieldType === 'userGroup') {
        if (localSelectedOptions.length > 0) {
          setHeaderFilters(prev => ({
            ...prev,
            [field.key]: localSelectedOptions
          }));
        } else {
          clearHeaderFilter(field.key);
        }
      } else {
        if (localFilterValue.trim()) {
          setHeaderFilters(prev => ({
            ...prev,
            [field.key]: localFilterValue.trim()
          }));
        } else {
          clearHeaderFilter(field.key);
        }
      }
      onClose();
    };

    const handleClear = () => {
      setFilterLoading(true);
      if (field.fieldType === 'date') {
        clearDateRangeFilter(field.key);
      } else if (field.fieldType === 'numberRange') {
        clearNumberRangeFilter(field.key);
      } else {
        clearHeaderFilter(field.key);
      }
      setLocalFilterValue('');
      setLocalSelectedOptions([]);
      setLocalDateRange({ from: '', to: '' });
      setLocalNumberRange({ min: '', max: '' });
      onClose();
    };

    if (!field) return null;

    const isFilterActive = field.fieldType === 'date'
      ? (dateRangeFilters[field.key] && (dateRangeFilters[field.key].from || dateRangeFilters[field.key].to))
      : field.fieldType === 'numberRange'
      ? (numberRangeFilters[field.key] && (numberRangeFilters[field.key].min !== undefined || numberRangeFilters[field.key].max !== undefined))
      : field.fieldType === 'dropdown' || field.fieldType === 'userGroup'
      ? (Array.isArray(headerFilters[field.key]) ? headerFilters[field.key].length > 0 : headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== '')
      : (headerFilters[field.key] && typeof headerFilters[field.key] === 'string' && headerFilters[field.key].trim() !== '');

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filter: {field.label}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {(field.fieldType === 'dropdown' || field.fieldType === 'userGroup') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Values (Multiple)
                </label>
                <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto p-2">
                  {getFieldOptions(field.key).map((option, index) => {
                    const optionValue = field.fieldType === 'userGroup' 
                      ? (typeof option === 'object' ? option : { id: option, name: option })
                      : option;
                    const optionId = field.fieldType === 'userGroup' ? optionValue.id : optionValue;
                    const optionName = field.fieldType === 'userGroup' ? optionValue.name : optionValue;
                    const optionType = field.fieldType === 'userGroup' ? optionValue.type : null;
                    const isSelected = field.fieldType === 'userGroup'
                      ? localSelectedOptions.some(opt => (typeof opt === 'object' ? opt.id : opt) === optionId)
                      : localSelectedOptions.includes(option);
                    
                    return (
                      <label
                        key={index}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleDropdownToggle(option)}
                          className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="flex-1">
                          <span className="text-sm text-gray-700">{optionName}</span>
                          {field.fieldType === 'userGroup' && optionType && (
                            <span className="ml-2 text-xs text-gray-500">({optionType})</span>
                          )}
                          {field.fieldType === 'userGroup' && optionValue.email && (
                            <div className="text-xs text-gray-500">{optionValue.email}</div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
                {localSelectedOptions.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {localSelectedOptions.length} option(s) selected
                  </div>
                )}
              </div>
            )}

            {field.fieldType === 'date' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={localDateRange.from}
                    onChange={(e) => setLocalDateRange(prev => ({ ...prev, from: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Date
                  </label>
                  <input
                    type="date"
                    value={localDateRange.to}
                    onChange={(e) => setLocalDateRange(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {field.fieldType === 'numberRange' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Days
                  </label>
                  <input
                    type="number"
                    value={localNumberRange.min}
                    onChange={(e) => setLocalNumberRange(prev => ({ ...prev, min: e.target.value }))}
                    placeholder="Min days"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Days
                  </label>
                  <input
                    type="number"
                    value={localNumberRange.max}
                    onChange={(e) => setLocalNumberRange(prev => ({ ...prev, max: e.target.value }))}
                    placeholder="Max days"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {(field.fieldType !== 'dropdown' && field.fieldType !== 'date') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter Value
                </label>
                <input
                  type="text"
                  value={localFilterValue}
                  onChange={(e) => setLocalFilterValue(e.target.value)}
                  placeholder={`Enter ${field.label}...`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            {isFilterActive && (
              <button
                onClick={handleClear}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-800"
              >
                Clear Filter
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      draft: 'bg-gray-100 text-gray-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: ClockIcon,
      in_progress: ExclamationTriangleIcon,
      completed: CheckCircleIcon,
      draft: DocumentTextIcon,
      rejected: XCircleIcon,
      cancelled: XCircleIcon
    };
    return icons[status] || ClockIcon;
  };

  const getTypeIcon = (type, item) => {
    // Check if this is a custom page submission
    if (item && item.originalData && item.originalData.customPage) {
      return DocumentTextIcon; // Use document icon for custom pages
    }
    return type === 'Form' ? DocumentTextIcon : ClipboardDocumentListIcon;
  };

  const handleViewDetails = (item) => {
    if (item.type === 'Form') {
      window.open(`/form-submissions/${item.id}`, '_blank');
    } else if (item.originalData && item.originalData.customPage) {
      // This is a custom page submission
      window.open(`/custom-page-submissions/${item.id}`, '_blank');
    } else {
      window.open(`/tasks/${item.id}`, '_blank');
    }
  };

  const formatDaysLeft = (daysLeft) => {
    if (daysLeft === null) return 'No deadline';
    if (daysLeft < 0) return `${Math.abs(daysLeft)} days overdue`;
    if (daysLeft === 0) return 'Due today';
    return `${daysLeft} days left`;
  };

  const getDaysLeftColor = (daysLeft) => {
    if (daysLeft === null || daysLeft === '') return 'text-gray-500';
    if (daysLeft < 0) return 'text-red-600 font-semibold';
    if (daysLeft <= 3) return 'text-orange-600 font-semibold';
    return 'text-green-600';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Forms & Tasks</h1>
        <p className="text-gray-600">Manage and track all forms and tasks in one place</p>
      </div>

      {/* Dashboard: Status + Deadline counts (one row, scroll if overflow) */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 overflow-hidden">
        <div className="dashboard-scroll flex items-center gap-6 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm font-semibold text-gray-700">Status:</span>
            <div className="flex flex-wrap items-center gap-2">
              {stats.statusCounts && Object.entries(stats.statusCounts).length > 0 ? (
                Object.entries(stats.statusCounts).map(([status, count]) => (
                  <span key={status} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                    {status.replace(/_/g, ' ')}: <strong className="ml-1">{count}</strong>
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">—</span>
              )}
            </div>
          </div>
          <span className="text-gray-300 shrink-0">|</span>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm font-semibold text-gray-700">Deadline:</span>
            <div className="flex flex-wrap items-center gap-2">
              {stats.deadlineCounts ? (
                <>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-50 text-red-800">
                    Overdue: <strong className="ml-1">{stats.deadlineCounts.overdue ?? 0}</strong>
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-amber-50 text-amber-800">
                    Today: <strong className="ml-1">{stats.deadlineCounts.today ?? 0}</strong>
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-800">
                    Next 7 days: <strong className="ml-1">{stats.deadlineCounts.next7 ?? 0}</strong>
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                    Next 30 days: <strong className="ml-1">{stats.deadlineCounts.next30 ?? 0}</strong>
                  </span>
                </>
              ) : (
                <span className="text-sm text-gray-500">—</span>
              )}
            </div>
          </div>
        </div>
        {/* Marquee-style scroll hint when content overflows */}
        <style>{`
          @keyframes scroll-hint {
            0%, 100% { opacity: 0.5; }
            50% { opacity: 1; }
          }
          .dashboard-scroll::-webkit-scrollbar { height: 6px; }
          .dashboard-scroll::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 3px; }
        `}</style>
      </div>

      {/* Table Controls Bar */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Table Controls */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          {/* Left side: Status Toggle Buttons */}
          <div className="flex items-center space-x-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700 mr-2">Status:</span>
            {uniqueStatuses.length > 0 ? uniqueStatuses.map((status) => {
              const isSelected = selectedStatuses.includes(status);
              return (
                <button
                  key={status}
                  onClick={() => toggleStatus(status)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
                </button>
              );
            }) : (
              <span className="text-sm text-gray-500">Loading statuses...</span>
            )}
          </div>

          {/* Right side: Pagination controls */}
          <div className="flex items-center space-x-4">
            {/* Show Rows Dropdown */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700">Show</label>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
                }}
                className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
              <label className="text-sm text-gray-700">Rows</label>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-700">
              {((pagination.page - 1) * pagination.limit) + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </div>

            {/* Filter Icon with Badge */}
            {(Object.keys(headerFilters).length > 0 || selectedStatuses.length > 0 || Object.keys(dateRangeFilters).length > 0 || Object.keys(numberRangeFilters).length > 0) && (
              <div className="relative">
                <button
                  onClick={clearAllFilters}
                  className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                  title="Clear all filters"
                >
                  <FunnelIcon className="h-5 w-5" />
                  <span className="absolute top-0 right-0 h-4 w-4 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                    {(Object.keys(headerFilters).length + selectedStatuses.length + Object.keys(dateRangeFilters).length + Object.keys(numberRangeFilters).length)}
                  </span>
                </button>
              </div>
            )}

            {/* Save View Button */}
            <button
              onClick={() => setShowSaveViewDialog(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              title="Save Current View"
            >
              <BookmarkIcon className="h-5 w-5" />
            </button>

            {/* Load View Button */}
            <button
              onClick={() => setShowLoadViewDialog(true)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md relative"
              title="Load Saved View"
            >
              <StarIcon className="h-5 w-5" />
              {savedViews.length > 0 && (
                <span className="absolute top-0 right-0 h-4 w-4 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                  {savedViews.length}
                </span>
              )}
            </button>

            {/* Column Visibility Icon */}
            <div className="relative">
              <button
                ref={columnVisibilityButtonRef}
                onClick={() => {
                  if (columnVisibilityButtonRef.current) {
                    const rect = columnVisibilityButtonRef.current.getBoundingClientRect();
                    setColumnVisibilityPosition({
                      top: rect.bottom + window.scrollY + 8,
                      right: window.innerWidth - rect.right + window.scrollX
                    });
                  }
                  setShowColumnVisibility(!showColumnVisibility);
                }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
                title="Column Visibility"
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
            </div>
            
            {/* Column Visibility Dropdown - Fixed positioning to avoid overflow clipping */}
            {showColumnVisibility && (
              <>
                {/* Backdrop to close dropdown */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowColumnVisibility(false)}
                />
                <div 
                  className="fixed w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto"
                  style={{
                    top: columnVisibilityPosition.top ? `${columnVisibilityPosition.top}px` : 'auto',
                    right: columnVisibilityPosition.right ? `${columnVisibilityPosition.right}px` : '16px',
                    bottom: columnVisibilityPosition.top ? 'auto' : '16px'
                  }}
                >
                  <div className="p-2">
                    <div className="text-xs font-semibold text-gray-700 mb-2 px-2">Column Visibility</div>
                    {tableFields.map((field) => (
                      <label 
                        key={field.key} 
                        className="flex items-center px-2 py-1 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={columnVisibility[field.key] !== false}
                          onChange={(e) => setColumnVisibility(prev => ({ ...prev, [field.key]: e.target.checked }))}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{field.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Pagination Arrows */}
            {pagination.total > 0 && (
              <div className="flex items-center space-x-1 border-l border-gray-300 pl-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-100"
                  title="Previous"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages || pagination.pages === 0}
                  className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded hover:bg-gray-100"
                  title="Next"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filter summary */}
        {(Object.keys(headerFilters).length > 0 || selectedStatuses.length > 0 || Object.keys(dateRangeFilters).length > 0 || Object.keys(numberRangeFilters).length > 0) && (
          <div className="px-4 py-2 bg-blue-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-blue-700 flex-wrap">
                <FunnelIcon className="h-4 w-4" />
                <span>Active filters:</span>
                {selectedStatuses.length > 0 && (
                  <span className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                    Status: {selectedStatuses.join(', ')}
                  </span>
                )}
                {Object.keys(headerFilters).map(key => {
                  const field = tableFields.find(f => f.key === key);
                  const filterValue = headerFilters[key];
                  if (Array.isArray(filterValue) && filterValue.length > 0) {
                    // For userGroup, show names instead of objects
                    const displayValues = filterValue.map(v => {
                      if (typeof v === 'object' && v.name) {
                        return v.name;
                      }
                      return v;
                    });
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: {displayValues.join(', ')}
                      </span>
                    );
                  } else if (filterValue && typeof filterValue === 'string' && filterValue.trim() !== '') {
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: "{filterValue}"
                      </span>
                    );
                  }
                  return null;
                })}
                {Object.keys(dateRangeFilters).map(key => {
                  const field = tableFields.find(f => f.key === key);
                  const range = dateRangeFilters[key];
                  if (range && (range.from || range.to)) {
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: {range.from || '...'} to {range.to || '...'}
                      </span>
                    );
                  }
                  return null;
                })}
                {Object.keys(numberRangeFilters).map(key => {
                  const field = tableFields.find(f => f.key === key);
                  const range = numberRangeFilters[key];
                  if (range && (range.min !== undefined || range.max !== undefined)) {
                    const minText = range.min !== undefined && range.min !== '' ? `${range.min}` : '...';
                    const maxText = range.max !== undefined && range.max !== '' ? `${range.max}` : '...';
                    return (
                      <span key={key} className="px-2 py-1 bg-blue-100 rounded text-blue-800">
                        {field?.label}: {minText} to {maxText} days
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
              <button
                onClick={clearAllFilters}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {getVisibleFields().map((field) => (
                  <th key={field.key} className="px-4 py-2">
                    <SortableHeader
                      field={field}
                      onSort={handleSort}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filterLoading ? (
                <tr>
                  <td colSpan={getVisibleFields().length} className="px-4 py-6 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Applying filters...</span>
                    </div>
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan={getVisibleFields().length} className="px-4 py-6 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={getVisibleFields().length} className="px-4 py-8 text-center">
                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No forms or tasks found</p>
                  </td>
                </tr>
              ) : (
                data.map((item) => (
                  <tr 
                    key={item.id} 
                    onClick={() => handleViewDetails(item)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {getVisibleFields().map((field) => (
                      <td key={field.key} className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {field.render(item)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filter Dialog */}
      {filterDialog.open && filterDialog.field && (
        <FilterDialog
          field={filterDialog.field}
          onClose={() => setFilterDialog({ open: false, field: null })}
        />
      )}

      {/* Save View Dialog */}
      {showSaveViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Save Current View</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">View Name</label>
              <input
                type="text"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder="Enter view name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="mb-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Set as default view</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">Default view will be automatically loaded when you open this page</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md mb-4">
              <p className="text-sm text-gray-700 font-medium mb-2">This view will save:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Column visibility settings</li>
                <li>• Sort field and direction</li>
              </ul>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setShowSaveViewDialog(false); setNewViewName(''); setSaveAsDefault(false); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                disabled={savingView}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveView}
                disabled={savingView || !newViewName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {savingView ? 'Saving...' : 'Save View'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load View Dialog */}
      {showLoadViewDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Load Saved View</h2>
            {loadingViews ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : savedViews.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No saved views yet</p>
                <p className="text-sm text-gray-500 mt-2">Save your current view settings to quickly access them later</p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedViews.map((view) => (
                  <div
                    key={view._id}
                    className={`border rounded-lg p-4 hover:bg-gray-50 transition-colors ${currentViewId === view._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{view.name}</h3>
                          {view.isDefault && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              <StarIconSolid className="h-3 w-3 mr-1" /> Default
                            </span>
                          )}
                          {currentViewId === view._id && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Active</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">Created: {new Date(view.createdAt).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button onClick={() => handleLoadView(view)} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Load</button>
                        {!view.isDefault && (
                          <button onClick={() => handleSetDefaultView(view._id)} className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200" title="Set as default">
                            <StarIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button onClick={() => handleDeleteView(view._id)} className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200">Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button onClick={() => setShowLoadViewDialog(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormsAndTasks; 